import { useState, useCallback, useRef } from "react";
import { useScanListener, useErrorListener } from "./useScanListener.ts";
import { ScanResult } from "./useCameraScanner.ts";
import { ProductItemWithDetails } from "utils/grocyApi.ts";
import { processScannedBarcode, BarcodeProcessResult } from "utils/stockDataProcessing.ts";
import { provideScanFeedback } from "utils/vibration.ts";

type ConsumptionMode = 'scanning' | 'consuming';
type ScanResultState = 'found' | 'no-stock' | 'not-found' | null;

interface UseBarcodeeScanningOptions {
  barcodeMap: Map<string, number>;
  productItemsMap: Map<number, ProductItemWithDetails>;
}

/**
 * Custom hook that handles barcode scanning logic and state management
 */
export function useBarcodeScanning({ barcodeMap, productItemsMap }: UseBarcodeeScanningOptions) {
  const [barcode, setBarcode] = useState<string>("");
  const [scannedProduct, setScannedProduct] = useState<{
    productItem: ProductItemWithDetails;
    barcode: string;
  } | null>(null);
  const [barcodeNotFound, setBarcodeNotFound] = useState<string | null>(null);
  const [consumptionMode, setConsumptionMode] = useState<ConsumptionMode>('scanning');
  const [scanResultState, setScanResultState] = useState<ScanResultState>(null);

  // Use refs to track state that needs immediate access in callbacks
  const consumptionModeRef = useRef<ConsumptionMode>('scanning');
  const isProcessingRef = useRef(false);

  // Handle scan results
  const handleScanResult = useCallback((result: BarcodeProcessResult) => {
    setBarcode(result.barcode);
    
    let shouldProvideFeedback = false;

    if (result.type === 'found' || result.type === 'no-stock') {
      setScannedProduct({
        productItem: result.productItem,
        barcode: result.barcode
      });
      setBarcodeNotFound(null);
      setScanResultState(result.type);
      shouldProvideFeedback = result.type === 'found' && result.productItem.stock.amount > 0;
    } else {
      setScannedProduct(null);
      setBarcodeNotFound(result.barcode);
      setScanResultState('not-found');
    }

    setConsumptionMode('consuming');
    consumptionModeRef.current = 'consuming';

    if (shouldProvideFeedback) {
      provideScanFeedback();
    }
    
    // Reset processing flag after state updates
    isProcessingRef.current = false;
  }, []);

  // Listen to scan events
  useScanListener(useCallback((scanResult: ScanResult) => {
    // Only process scans when in scanning mode and not already processing
    if (consumptionModeRef.current !== 'scanning' || isProcessingRef.current) {
      return;
    }

    // Set processing flag immediately to prevent rapid-fire scans
    isProcessingRef.current = true;

    const result = processScannedBarcode(
      scanResult.text,
      barcodeMap,
      productItemsMap
    );
    
    handleScanResult(result);
  }, [barcodeMap, productItemsMap, handleScanResult]));

  // Listen to error events
  useErrorListener(useCallback((errorMessage: string) => {
    console.log("Camera error:", errorMessage);
  }, []));

  // Clear scan state and return to scanning mode
  const clearScan = useCallback(() => {
    setScannedProduct(null);
    setBarcodeNotFound(null);
    setConsumptionMode('scanning');
    consumptionModeRef.current = 'scanning';
    setScanResultState(null);
    setBarcode("");
    // Reset processing flag when clearing
    isProcessingRef.current = false;
  }, []);

  return {
    // State
    barcode,
    scannedProduct,
    barcodeNotFound,
    consumptionMode,
    scanResultState,
    
    // Actions
    clearScan,
  };
} 
